import type { InternalSerializeQueryArgs, QueryArgFrom, QueryActionCreatorResult } from '../rtk-query-types';
import type { AnyApi } from '../buildCreateApi';
import type { GetStoreFn } from '../types';
import { prefetchManager } from './prefetchManager';
import type { AnyEndpointDefinition, AnyQueryDefinition, AnyEndpointQuery } from './types';

// Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
export type EndpointPrefetch<Definition extends AnyQueryDefinition> = {
    /**
     * @soft-deprecated Метод скоро будет удален. Используйте endpoint.query()
     */
    prefetch: (queryArgs: QueryArgFrom<Definition>, options?: PrefetchOptions) => QueryActionCreatorResult<Definition>;
};

// Параметры для `buildEndpointPrefetch()`.
type BuildPrefetchOptions = {
    api: AnyApi;
    endpointName: string;
    getStore: GetStoreFn;
    buildPrefetchKey: PrefetchKeyBuilder;
};

// Параметры запроса endpoint.prefetch(arg, options)
type PrefetchOptions = {
    /**
     * Флаг, который позволяет сделать запрос даже в том случае, если в кеше есть данные.
     */
    forceRefetch?: boolean;

    /**
     * Возраст данных в кеше (в секундах), после которого нужно перезапросить данные.
     * Если в кеше есть достаточно свежие данные, то функция вернет закешированный результат без запроса в бекенд.
     */
    refetchIfOlderThan?: number;

    /**
     * Количество секунд, в течении которых в сторе будет хранится подписка на данные,
     * полученные в результате вызова prefetch. Подписка будет удалена по окончании указанного времени, либо при первом
     * монтировании компонента, использующего эти данные.
     *
     * @default 60
     */
    keepPrefetchedDataFor?: number;
};

// Метод для генерации prefetch метода эндпоинта, который сохраняет промис в promiseStorage.
export function buildEndpointPrefetch({
    api,
    endpointName,
    getStore,
    buildPrefetchKey,
}: BuildPrefetchOptions): EndpointPrefetch<AnyQueryDefinition>['prefetch'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = api.endpoints[endpointName] as AnyEndpointQuery;

    return function prefetch(queryArgs, options) {
        const prefetchKey = buildPrefetchKey(queryArgs);

        const promise = getStore().dispatch(
            endpoint.initiate(queryArgs, {
                forceRefetch: options?.forceRefetch || options?.refetchIfOlderThan,
            })
        );
        prefetchManager.set(prefetchKey, {
            promise,
            keepPrefetchedDataFor: options?.keepPrefetchedDataFor,
        });

        return promise;
    };
}

/**
 * Функция для вычисления ключа кеширования для prefetch промисов.
 */
export type PrefetchKeyBuilder = (queryArgs: QueryArgFrom<AnyQueryDefinition>) => string;
export function buildPrefetchKeyBuilder({
    api,
    endpointName,
    endpointDefinition,
    serializeQueryArgs,
}: {
    api: AnyApi;
    endpointName: string;
    endpointDefinition: AnyEndpointDefinition;
    serializeQueryArgs: InternalSerializeQueryArgs;
}) {
    return (queryArgs: QueryArgFrom<AnyQueryDefinition>) => {
        const queryCacheKey = serializeQueryArgs({ endpointDefinition, endpointName, queryArgs });
        return `${api.reducerPath}/${queryCacheKey}`;
    };
}
